import { Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, Select, TOAST_TYPE } from '@base'
import { CountryCodes, INSPECTION_USER_DESIGNATIONS, strings } from '@constants'
import React, { useEffect, useMemo, useState } from 'react'
import { InputWrapper } from '../PreviewPO/SiteManagerDetails'
import { Spacings } from '@styles'
import { useDispatch, useSelector } from 'react-redux'
import { getCountryDialCode, getNumberWithCountryCode, mobileLength } from '@utils'
import { showLoader, showToast } from '@data/state/action'
import { editEnquiry, getUsersOfOrganisation, updateEnquiryCostMetrics, updateOrderCostMetrics } from '@data'
import { User } from '@data/model'

const initialFormState = {
	full_name: {
		id: 'full_name',
		label: 'Name',
		placeholder: 'Enter full name',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	country_code: {
		key: 'country_code',
		value: CountryCodes[0],
		valid: true,
	},
	mobile_number: {
		id: 'mobile_number',
		label: 'Mobile',
		placeholder: 'Enter mobile number',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	email: {
		id: 'email',
		label: 'Email',
		placeholder: 'Enter email',
		value: '',
		dirty: false,
		required: true,
	},
	designation: {
		id: 'designation',
		label: 'Designation',
		placeholder: 'Enter designation',
		value: '',
		dirty: false,
		required: false,
	},
}

const deriveFormStateFromItemData = (oldState = initialFormState, data) => {
	const derivedState = { ...oldState }
	const dialCode = getCountryDialCode(data?.country_code || defaultCountryCode)

	derivedState.full_name = {
		...derivedState.full_name,
		value: data?.full_name || '',
		dirty: false,
	}
	derivedState.country_code = {
		...derivedState.country_code,
		value: CountryCodes.find((item) => item.code === data?.country_code) || defaultCountryCode,
		valid: true,
	}
	derivedState.mobile_number = {
		...derivedState.mobile_number,
		value: data?.mobile_number?.replace(dialCode, '')?.trim() || '',
		dirty: false,
		valid: null,
	}
	derivedState.email = {
		...derivedState.email,
		value: data?.email_id || data?.email || '',
		dirty: false,
	}
	derivedState.designation = {
		...derivedState.designation,
		value: data?.designation || '',
		dirty: false,
	}

	return derivedState
}

const defaultCountryCode = CountryCodes.find((_c) => _c.default)
const designationTypes = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, value]) => ({
	key,
	label: value,
}))

const isValidEmail = (email) => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const PurchaseManagerDetails = ({ enquiry, update, userId, order, editable, organisationId = null, globalUpdateState }) => {
	const dispatch = useDispatch()

	const [countryCode, setCountryCode] = useState(defaultCountryCode)
	const [formState, setFormState] = useState(initialFormState)
	const [userList, setUserList] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)
	const [mobileValid, setMobileValid] = useState(false)
	const user = useSelector((_state) => _state.getIn(['user', 'user']))

	useEffect(() => {
		const initialData = order?.cost_metrics?.buyer_procurement_manager || enquiry?.cost_metrics?.buyer_procurement_manager
		if (initialData) {
			const derivedState = deriveFormStateFromItemData(initialFormState, initialData)
			setFormState(derivedState)
			setCountryCode(derivedState.country_code.value)
		}
	}, [order, enquiry])

	useEffect(() => {
		if (!organisationId) return

		dispatch(showLoader(true))
		getUsersOfOrganisation(organisationId)
			.then((response) => {
				setUserList(response || [])
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message ?? strings('msg_failed_to_fetch')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [organisationId])

	const handleUserSelect = (value) => {
		setFormState((_oldState) => deriveFormStateFromItemData(_oldState, value))
		setSelectedUser(value)
	}

	const updateState = (key, updates) => {
		setFormState((_oldState) => ({
			..._oldState,
			[key]: {
				..._oldState[key],
				...updates,
			},
		}))
	}

	const handleDesignationChange = (value) => {
		updateState('designation', { value: value?.key ?? '' })
	}

	const isUserSelectDisabled = useMemo(() => {
		const { full_name, mobile_number, email, designation } = formState
		return !selectedUser && Boolean(full_name.value || mobile_number.value || email.value || designation.value)
	}, [formState.full_name.value, formState.mobile_number.value, formState.email.value, formState.designation.value])

	const areAllFieldsDisabled = !!selectedUser

	const submitHandler = () => {
		const { full_name, country_code, mobile_number, email, designation } = formState

		if (!formState.email.value || !isValidEmail(formState.email.value)) {
			dispatch(showToast(true, 'Please enter a valid email', { type: TOAST_TYPE.ERROR }))
			return
		}

		const updatedCostMetrics = order
			? {
					...order.cost_metrics,
					buyer_procurement_manager: {
						full_name: full_name.value,
						mobile_number: getNumberWithCountryCode(country_code?.value, mobile_number.value),
						email: email.value,
						designation: designation.value,
						country_code: country_code.value.code,
					},
				}
			: {
					...enquiry.cost_metrics,
					buyer_procurement_manager: {
						full_name: full_name.value,
						mobile_number: getNumberWithCountryCode(country_code?.value, mobile_number.value),
						email: email.value,
						designation: designation.value,
						country_code: country_code.value.code,
					},
				}

		dispatch(showLoader(true))
		const API = order
			? updateOrderCostMetrics(order?.order_id, user?.user_id, updatedCostMetrics)
			: updateEnquiryCostMetrics(enquiry?.enquiry_id, user?.user_id, updatedCostMetrics)

		API?.then((response) => {
			dispatch(showToast(true, strings(['purchase', 'manager', 'details', 'updated', 'successfully']), { type: TOAST_TYPE.SUCCESS }))
			update()
			globalUpdateState && globalUpdateState()
		})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const resetHandler = () => {
		setFormState(initialFormState)
		setSelectedUser(null)
		setCountryCode(defaultCountryCode)
	}

	const disabledHandler = useMemo(() => {
		if (selectedUser) return false
		return (
			!formState.full_name.value ||
			!formState.mobile_number.value ||
			!formState.email.value ||
			!mobileValid ||
			!isValidEmail(formState.email.value)
		)
	}, [formState.full_name.value, formState.mobile_number.value, formState.email.value, mobileValid, selectedUser])

	return (
		<>
			<DrawerBodyWrapper>
				<Select
					id='select-user'
					label={strings('user')}
					placeholder={strings('select', 'user')}
					data={userList ?? []}
					value={userList?.find((item) => item.user_id === selectedUser?.user_id) ?? {}}
					primaryKey='user_id'
					displayKey='full_name'
					onChange={handleUserSelect}
					disabled={isUserSelectDisabled}
				/>

				<Input
					id={formState.full_name.id}
					label={formState.full_name.label}
					placeholder={formState.full_name.placeholder}
					value={formState.full_name.value ?? ''}
					required={formState.full_name.required}
					onChange={(value) => {
						updateState('full_name', { value, dirty: true })
					}}
					disabled={areAllFieldsDisabled}
				/>

				<div style={{ display: 'flex' }}>
					<InputWrapper margin={`0 ${Spacings.SPACING_2B} 0 0`} width={4}>
						<Select
							id='login-country-code'
							data={CountryCodes}
							displayKey='dial_code'
							primaryKey='name'
							type='tel'
							noDropIcon
							value={CountryCodes.find((item) => formState.country_code.value?.code === item.code) ?? CountryCodes[0]}
							onChange={(value) => updateState('country_code', { value, dirty: true })}
							disabled={areAllFieldsDisabled}
						/>
					</InputWrapper>
					<InputWrapper>
						<Input
							id={formState.mobile_number.id}
							type='tel'
							label={formState.mobile_number.label}
							placeholder={formState.mobile_number.placeholder}
							value={formState.mobile_number.value ?? ''}
							setValidity={setMobileValid}
							pattern='^\d{7}\d+$'
							maxLength={mobileLength(countryCode.dial_code)}
							errorMap={{
								patternMismatch: `${strings('enter_valid_num')}`,
								valueMissing: `${strings('mob_num_required')}`,
							}}
							required={formState.mobile_number.required}
							onChange={(value) => {
								updateState('mobile_number', { value, dirty: true })
							}}
							hasError={(error) => {
								setFormState((_s) => ({
									..._s,
									mobile_number: {
										..._s.mobile_number,
										valid: !error,
									},
								}))
							}}
							disabled={areAllFieldsDisabled}
						/>
					</InputWrapper>
				</div>

				<Input
					id={formState.email.id}
					label={formState.email.label}
					type='email'
					pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
					errorMap={{
						patternMismatch: `${strings('enter_valid_email')}`,
						valueMissing: `Email is required`,
					}}
					placeholder={formState.email.placeholder}
					value={formState.email.value}
					onChange={(value) => {
						updateState('email', { value, dirty: true })
					}}
					required={true}
					hasError={(error) => {
						setFormState((_s) => ({
							..._s,
							email: {
								..._s.email,
								valid: !error,
							},
						}))
					}}
					disabled={selectedUser?.email_id}
				/>

				<Select
					id='select-designation'
					label={strings('designation')}
					placeholder={strings('select', 'designation')}
					data={designationTypes ?? []}
					value={designationTypes.find((item) => item.key === formState.designation.value) ?? {}}
					disabled={areAllFieldsDisabled}
					primaryKey='key'
					displayKey='label'
					required={formState.designation.required}
					onChange={handleDesignationChange}
				/>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button margin={`0 0 0 auto`} small disabled={disabledHandler} onClick={submitHandler}>
					{editable ? strings('update') : strings('add')}
				</Button>
				<Button small type='tertiary' variant='destructive' onClick={resetHandler} margin={`0 0 0 ${Spacings.SPACING_4B}`}>
					{strings('reset')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default PurchaseManagerDetails
